import { useCallback, useEffect, useState } from "react";
import { Download, Eye, FileText, Plus, RefreshCw, Trash2 } from "lucide-react";
import { adminApi, errText } from "./adminApi";

const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;

export default function AdminMockTests() {
  const [data, setData] = useState(null);
  const [papers, setPapers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", mode: "pdf", paper_id: "", topic_id: "", total: 45, duration_minutes: 60, per_question_seconds: 0, shuffle: true, navigation: true, preserve_numbering: false, marks_correct: 4, marks_negative: -1, pdf_count: 23 });
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [attempts, setAttempts] = useState(null);

  const load = useCallback(async () => {
    const [{ data: t }, { data: p }, { data: top }] = await Promise.all([
      adminApi.get("/admin/mock-tests"), adminApi.get("/admin/papers"), adminApi.get("/admin/test/topics")]);
    setData(t);
    setPapers(p.papers.filter((x) => x.status === "ready"));
    setTopics(top.topics);
    return t;
  }, []);
  useEffect(() => { load().catch(() => setError("Could not load mock tests.")); }, [load]);

  useEffect(() => {
    if (!data?.tests?.some((t) => t.status === "building")) return undefined;
    const t = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [data, load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    setCreating(true);
    setError("");
    try {
      await adminApi.post("/admin/mock-tests", {
        name: form.name, mode: form.mode, paper_id: form.paper_id, topic_id: form.topic_id,
        config: { total: form.total, duration_minutes: form.duration_minutes, per_question_seconds: form.per_question_seconds,
                  shuffle: form.shuffle, navigation: form.navigation, preserve_numbering: form.preserve_numbering,
                  marks_correct: form.marks_correct, marks_negative: form.marks_negative,
                  pdf_count: form.mode === "hybrid" ? form.pdf_count : undefined },
      });
      setNotice("Test created — questions are being assembled in the background.");
      setShowCreate(false);
      await load();
    } catch (err) { setError(errText(err)); }
    setCreating(false);
  };

  const togglePublish = async (t) => {
    try {
      await adminApi.put(`/admin/mock-tests/${t.test_id}`, { published: !t.published });
      setNotice(t.published ? "Test unpublished." : "Test published — students can now buy and attempt it.");
      await load();
    } catch (err) { setError(errText(err)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Its attempts stay in records.`)) return;
    await adminApi.delete(`/admin/mock-tests/${t.test_id}`);
    await load();
  };

  const viewQuestions = async (t) => {
    if (preview?.test.test_id === t.test_id) { setPreview(null); return; }
    const { data } = await adminApi.get(`/admin/mock-tests/${t.test_id}/questions`);
    setPreview(data);
    setAttempts(null);
  };

  const viewAttempts = async (t) => {
    const { data } = await adminApi.get(`/admin/mock-tests/${t.test_id}/attempts`);
    setAttempts({ test: t, attempts: data.attempts });
    setPreview(null);
  };

  const downloadCsv = (t) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/admin/mock-tests/${t.test_id}/results.csv`, "_blank");
  };

  return (
    <div data-testid="admin-mock-tests">
      <div className="admin-row" style={{ justifyContent: "space-between" }}>
        <div>
          <h4 style={{ marginBottom: 4 }}>Mock Tests (full-length, {inr(data?.price_paise ?? 2100)}/test)</h4>
          <span className="lead-sub">45-question paid tests. Free with the {inr(9900)} monthly Test Pass ({data?.pass_free_per_topic ?? 3} free per topic per month). Build from an uploaded paper (PDF mode), the AI engine (AI mode), or a mix (Hybrid).</span>
        </div>
        <button className="admin-btn primary" onClick={() => setShowCreate(!showCreate)} data-testid="mt-create-open"><Plus size={14} /> New test</button>
      </div>
      {error && <p className="admin-error" data-testid="mt-error">{error}</p>}
      {notice && <p className="tp-success-box" style={{ background: "#123a2e", border: "1px solid #12b76a", color: "#4ade80" }}>{notice}</p>}

      {showCreate && <div className="admin-card" style={{ marginTop: 14 }} data-testid="mt-create-form">
        <h4>Create mock test</h4>
        <div className="admin-row" style={{ flexWrap: "wrap", marginTop: 10 }}>
          <input className="admin-input" style={{ flex: 1, minWidth: 220 }} placeholder="Test name (e.g. Current Electricity Full Test)" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="mt-name" />
          <select className="admin-input" value={form.mode} onChange={(e) => set("mode", e.target.value)} data-testid="mt-mode">
            <option value="pdf">PDF-based (from uploaded paper)</option>
            <option value="ai">AI-generated (bank + fresh AI top-up)</option>
            <option value="hybrid">Hybrid (PDF + AI)</option>
          </select>
          {form.mode !== "ai" && <select className="admin-input" value={form.paper_id} onChange={(e) => set("paper_id", e.target.value)} data-testid="mt-paper">
            <option value="">Select paper…</option>
            {papers.map((p) => <option key={p.paper_id} value={p.paper_id}>{p.name} ({p.extracted} Qs)</option>)}
          </select>}
          {form.mode !== "pdf" && <select className="admin-input" value={form.topic_id} onChange={(e) => set("topic_id", e.target.value)} data-testid="mt-topic">
            <option value="">Select topic for AI…</option>
            {topics.filter((t) => t.published).map((t) => <option key={t.topic_id} value={t.topic_id}>{t.name}</option>)}
          </select>}
        </div>
        <div className="admin-row" style={{ flexWrap: "wrap", marginTop: 10 }}>
          <label className="admin-label">QUESTIONS<input type="number" className="admin-input" style={{ width: 90 }} value={form.total} onChange={(e) => set("total", Number(e.target.value))} data-testid="mt-total" /></label>
          <label className="admin-label">DURATION (MIN)<input type="number" className="admin-input" style={{ width: 90 }} value={form.duration_minutes} onChange={(e) => set("duration_minutes", Number(e.target.value))} data-testid="mt-duration" /></label>
          <label className="admin-label">PER-Q TIMER (SEC, 0=OFF)<input type="number" className="admin-input" style={{ width: 130 }} value={form.per_question_seconds} onChange={(e) => set("per_question_seconds", Number(e.target.value))} data-testid="mt-perq" /></label>
          <label className="admin-label">+MARKS<input type="number" className="admin-input" style={{ width: 80 }} value={form.marks_correct} onChange={(e) => set("marks_correct", Number(e.target.value))} data-testid="mt-marks-correct" /></label>
          <label className="admin-label">−MARKS<input type="number" className="admin-input" style={{ width: 80 }} value={form.marks_negative} onChange={(e) => set("marks_negative", Number(e.target.value))} data-testid="mt-marks-negative" /></label>
          {form.mode === "hybrid" && <label className="admin-label">FROM PDF<input type="number" className="admin-input" style={{ width: 90 }} value={form.pdf_count} onChange={(e) => set("pdf_count", Number(e.target.value))} data-testid="mt-pdfcount" /></label>}
        </div>
        <div className="admin-row" style={{ marginTop: 6 }}>
          <label className="check-row"><input type="checkbox" checked={form.shuffle} onChange={(e) => set("shuffle", e.target.checked)} data-testid="mt-shuffle" /> <span>Shuffle questions</span></label>
          <label className="check-row"><input type="checkbox" checked={form.navigation} onChange={(e) => set("navigation", e.target.checked)} data-testid="mt-nav" /> <span>Allow question navigation</span></label>
          <label className="check-row"><input type="checkbox" checked={form.preserve_numbering} onChange={(e) => set("preserve_numbering", e.target.checked)} data-testid="mt-numbering" /> <span>Preserve paper numbering</span></label>
          <button className="admin-btn primary" disabled={creating || !form.name || (form.mode !== "ai" && !form.paper_id) || (form.mode !== "pdf" && !form.topic_id)} onClick={create} data-testid="mt-create-btn">{creating ? "Creating…" : "Create test"}</button>
        </div>
      </div>}

      {data?.tests?.length === 0 && <div className="admin-empty" data-testid="mt-empty">No mock tests yet. Upload a paper in the Papers tab, then create your first test.</div>}
      {data?.tests?.map((t, i) => (
        <div className="admin-card" key={t.test_id} data-testid={`mt-row-${i}`}>
          <div className="admin-row">
            <span className={`status-pill ${t.status === "draft" ? "approved" : t.status === "failed" ? "failed" : "review"}`} data-testid={`mt-status-${i}`}>
              {t.status === "building" ? (t.progress || "BUILDING…") : t.status.toUpperCase()}</span>
            <span className={`status-pill ${t.published ? "paid" : "locked"}`} data-testid={`mt-pub-${i}`}>{t.published ? "PUBLISHED" : "UNPUBLISHED"}</span>
            <span className="status-pill review">{t.mode.toUpperCase()}</span>
            <b style={{ color: "#eef4fc", flex: 1 }}>{t.name}</b>
          </div>
          <p className="lead-sub" style={{ marginTop: 6 }} data-testid={`mt-meta-${i}`}>
            {t.question_ids?.length || 0} questions · {t.config.duration_minutes} min · {t.config.per_question_seconds ? `${t.config.per_question_seconds}s/question · ` : ""}{t.config.marks_correct}/{t.config.marks_negative} marking · {t.mode !== "ai" ? `paper: ${t.paper_name} · ` : ""}{t.mode !== "pdf" ? `topic: ${t.topic_name} · ` : ""}{t.attempts} attempt(s)</p>
          {t.error && <p className="admin-error">{t.error}</p>}
          <div className="admin-row" style={{ marginTop: 8 }}>
            {t.status === "draft" && <button className="admin-btn" onClick={() => togglePublish(t)} data-testid={`mt-publish-${i}`}>{t.published ? "Unpublish" : "Publish"}</button>}
            {t.status === "draft" && <button className="admin-btn" onClick={() => viewQuestions(t)} data-testid={`mt-preview-${i}`}><Eye size={13} /> Questions</button>}
            <button className="admin-btn" onClick={() => viewAttempts(t)} data-testid={`mt-attempts-${i}`}><FileText size={13} /> Attempts ({t.attempts})</button>
            <button className="admin-btn" onClick={() => downloadCsv(t)} data-testid={`mt-csv-${i}`}><Download size={13} /> CSV</button>
            <button className="admin-btn" onClick={() => remove(t)} data-testid={`mt-delete-${i}`}><Trash2 size={13} /></button>
            <button className="admin-btn" onClick={() => load()}><RefreshCw size={13} /></button>
          </div>
          {preview?.test.test_id === t.test_id && <div style={{ marginTop: 10, maxHeight: 400, overflowY: "auto" }} data-testid="mt-preview">
            {preview.questions.map((q, j) => <div key={q.question_id} className="admin-row" style={{ borderTop: "1px solid #22314a", padding: "6px 0" }}>
              <span className="status-pill locked">Q{j + 1}</span>
              <span className={`status-pill ${q.source === "pdf" ? "review" : q.source === "ai" ? "approved" : "locked"}`}>{(q.source || "curated").toUpperCase()}</span>
              <span style={{ flex: 1, color: "#dbe6f4", fontSize: 12 }}>{q.text.slice(0, 110)}{q.text.length > 110 ? "…" : ""}</span>
              <span className="lead-sub">Ans: {q.correct}</span>
            </div>)}
          </div>}
          {attempts?.test.test_id === t.test_id && <div style={{ marginTop: 10 }} data-testid="mt-attempts-panel">
            {attempts.attempts.length === 0 && <div className="admin-empty">No attempts yet.</div>}
            {attempts.attempts.map((a, j) => <div key={a.attempt_id} className="admin-row" style={{ borderTop: "1px solid #22314a", padding: "6px 0" }} data-testid={`mt-attempt-${j}`}>
              <b style={{ color: "#eef4fc" }}>{a.student_name}</b>
              <span className="lead-sub">{a.student_whatsapp}</span>
              <span className="status-pill approved">{a.result.score}/{a.result.max_score}</span>
              <span className="lead-sub">{a.result.accuracy}% · {Math.round(a.result.time_taken_sec / 60)} min · {a.paid_via}</span>
            </div>)}
          </div>}
        </div>
      ))}
    </div>
  );
}
